import { useEffect, useState } from "react";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

export const TABS = [
  "General",
  "Catalogue",
  "Edges",
  "Corners",
  "Bumps",
  "Cutouts",
  "Kits",
  "Lines",
];

// tiny cross-section of each edge profile, drawn 0..24 x 0..14
export const SHAPE_PATH = {
  eased: "M2 3 H22 V11 H2 Z",
  bevel: "M2 3 H18 L22 7 V11 H2 Z",
  "half-bullnose": "M2 3 H18 Q22 3 22 7 V11 H2 Z",
  bullnose: "M2 3 H16 Q22 3 22 7 Q22 11 16 11 H2 Z",
  ogee: "M2 3 H16 Q20 3 18 7 Q16 11 22 11 H2 Z",
  "round-over": "M2 3 H19 Q22 3 22 6 V11 H2 Z",
  "double-round-over": "M2 3 H19 Q22 3 22 6 Q22 9 19 11 H2 Z",
  mitered: "M2 3 H22 V11 H18 L18 5 H2 Z",
  chisel: "M2 3 H18 L22 5 L18 7 L22 9 L18 11 H2 Z",
  pencil: "M2 3 H20 Q22 5 22 7 Q22 9 20 11 H2 Z",
  cove: "M2 3 H22 Q18 7 22 11 H2 Z",
  waterfall: "M2 3 H22 V11 H14 V14 H10 V11 H2 Z",
  unfinished: "M2 3 H22 V11 H2 Z",
};

const HEX_RE = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

// Color chooser: preset swatches + OS color picker + free hex entry.
export const ColorPicker = ({ value, onChange, options = [], testId }) => {
  const [draft, setDraft] = useState(value || "");
  useEffect(() => setDraft(value || ""), [value]);
  const commit = (raw) => {
    const v = (raw || "").trim();
    const hex = v && !v.startsWith("#") ? `#${v}` : v;
    if (HEX_RE.test(hex)) onChange(hex.toLowerCase());
    else setDraft(value || "");
  };
  const swatches = value && !options.includes(value) ? [value, ...options] : options;
  const native = HEX_RE.test(value || "") ? value : "#000000";
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          className="flex h-10 w-16 items-center justify-center rounded border border-zinc-800 bg-zinc-950 transition-colors duration-150 hover:border-zinc-600"
        >
          <span className="block h-4 w-6 rounded" style={{ background: value }} />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-60 border-zinc-800 bg-zinc-900 p-3 text-zinc-200">
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-6" data-testid={`${testId}-swatches`}>
          {swatches.map((c) => (
            <button
              key={c}
              type="button"
              title={c}
              data-testid={`${testId}-swatch`}
              onClick={() => onChange(c)}
              className={`h-6 w-6 rounded ring-offset-1 ring-offset-zinc-900 transition-transform duration-150 hover:scale-110 ${
                (value || "").toLowerCase() === (c || "").toLowerCase()
                  ? "ring-2 ring-white"
                  : "ring-1 ring-white/20"
              }`}
              style={{ background: c }}
            />
          ))}
        </div>
        <div className="mt-3 border-t border-zinc-800 pt-3">
          <div className="mb-1.5 text-[10px] font-bold uppercase tracking-widest text-zinc-500">
            Custom color
          </div>
          <div className="flex items-center gap-2">
            <input
              type="color"
              aria-label="Pick a custom color"
              data-testid={`${testId}-native`}
              value={native}
              onChange={(ev) => onChange(ev.target.value)}
              className="h-9 w-10 shrink-0 cursor-pointer rounded border border-zinc-700 bg-transparent p-0.5"
            />
            <Input
              data-testid={`${testId}-hex`}
              value={draft}
              onChange={(ev) => setDraft(ev.target.value)}
              onBlur={() => commit(draft)}
              onKeyDown={(ev) => {
                if (ev.key === "Enter") {
                  ev.preventDefault();
                  commit(draft);
                }
              }}
              placeholder="#RRGGBB"
              className="font-mono-data h-9 flex-1 border-zinc-800 bg-zinc-950 text-xs"
            />
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const ShapeIcon = ({ shape, color }) => (
  <svg viewBox="0 0 24 14" className="h-8 w-12 shrink-0">
    <rect width="24" height="14" rx="2" fill="#09090b" />
    <path
      d={SHAPE_PATH[shape] || SHAPE_PATH.eased}
      fill={shape === "unfinished" ? "none" : `${color}33`}
      stroke={color}
      strokeWidth="1"
      strokeDasharray={shape === "unfinished" ? "2 1.5" : undefined}
    />
  </svg>
);

// L-shaped sample used by the area and measurement pickers
export const L_SHAPE = "M6 6 H54 V22 H30 V38 H6 Z";

export const AREA_ART = {
  actual: (
    <>
      <path d={L_SHAPE} fill="#6366f1" fillOpacity="0.55" stroke="#a5b4fc" />
      <path
        d="M30 22 H54 V38 H30 Z"
        fill="none"
        stroke="#52525b"
        strokeDasharray="2 2"
      />
    </>
  ),
  gross: (
    <>
      <path d={L_SHAPE} fill="#6366f1" fillOpacity="0.55" stroke="#a5b4fc" />
      <path d="M30 22 H54 V38 H30 Z" fill="#6366f1" fillOpacity="0.22" />
    </>
  ),
  outline: (
    <>
      <path d={L_SHAPE} fill="#6366f1" fillOpacity="0.4" stroke="#a5b4fc" />
      <path
        d="M6 6 H54 V22 H30 V38 H6 Z"
        fill="none"
        stroke="#22d3ee"
        strokeDasharray="3 2"
      />
    </>
  ),
  bbox: (
    <>
      <path d={L_SHAPE} fill="#6366f1" fillOpacity="0.4" stroke="#a5b4fc" />
      <rect
        x="6"
        y="6"
        width="48"
        height="32"
        fill="none"
        stroke="#22d3ee"
        strokeDasharray="3 2"
      />
    </>
  ),
};

export const DIM_ART = {
  main: ["M6 2 H54", "M2 6 V38"],
  main_bumps: ["M6 2 H54", "M2 6 V38", "M30 42 H54"],
  segments: ["M6 2 H30", "M30 2 H54", "M2 6 V22", "M2 22 V38", "M30 42 H54"],
  combined: ["M6 2 H54", "M2 6 V38", "M30 18 H54", "M58 22 V38", "M30 42 H54"],
};

export const PickCard = ({ testId, active, label, hint, children, onClick }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`flex flex-col gap-2 rounded-xl border p-3 text-left transition-all duration-200 ${
      active
        ? "border-indigo-400/70 bg-indigo-500/10 shadow-[0_0_26px_-12px_rgba(99,102,241,0.9)]"
        : "border-zinc-800 bg-zinc-950 hover:border-zinc-700"
    }`}
  >
    <span className="flex items-center gap-2">
      <span
        className={`flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
          active ? "border-indigo-400" : "border-zinc-600"
        }`}
      >
        {active && <span className="h-2 w-2 rounded-full bg-indigo-400" />}
      </span>
      <span
        className={`text-sm font-semibold ${active ? "text-white" : "text-zinc-300"}`}
      >
        {label}
      </span>
    </span>
    <span className="block text-xs leading-relaxed text-zinc-500">{hint}</span>
    <svg viewBox="0 0 62 46" className="mt-1 h-20 w-full">
      {children}
    </svg>
  </button>
);

export const Row = ({ label, hint, children }) => (
  <label className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 py-3 last:border-b-0">
    <span className="min-w-0">
      <span className="block text-sm font-semibold text-zinc-200">{label}</span>
      {hint && <span className="block text-xs text-zinc-500">{hint}</span>}
    </span>
    {children}
  </label>
);

export const Num = ({ value, onChange, testId, suffix }) => (
  <span className="flex items-center gap-2">
    <Input
      data-testid={testId}
      type="number"
      step="0.01"
      value={value ?? 0}
      onChange={(e) => onChange(Number(e.target.value))}
      className="font-mono-data h-11 w-28 border-zinc-800 bg-zinc-950 text-right text-sm"
    />
    {suffix && <span className="text-xs text-zinc-500">{suffix}</span>}
  </span>
);

export const KitNum = ({ value, onChange, testId }) => (
  <Input
    data-testid={testId}
    type="number"
    step="0.5"
    value={value ?? 0}
    onChange={(e) => onChange(Number(e.target.value))}
    className="font-mono-data h-10 w-16 border-zinc-800 bg-zinc-950 text-right text-sm"
  />
);
